import React, { useState } from 'react';
import { Form, Input, Button, message, Tabs, Divider } from 'antd';
import { GoogleOutlined, UserOutlined, LockOutlined, MailOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../AuthProvider';
import './auth.css';

const SignUpScreen = () => {
  const [form] = Form.useForm();
  const [tenantType, setTenantType] = useState('employer');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await signup(values.email, values.password, tenantType);
      message.success({
        content: 'Account created successfully! Please check your email to verify your account.',
        duration: 4,
      });
      // Navigate to dashboard - middleware will handle email verification
      navigate(tenantType === 'agency' ? '/agency' : '/employer', { 
        state: { fromSignup: true } 
      });
    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          message.error('This email is already in use. Please use a different email.');
          break;
        case 'auth/invalid-email':
          message.error('The email address is not valid.');
          break;
        case 'auth/weak-password':
          message.error('The password is too weak. Please use a stronger password.');
          break;
        case 'auth/operation-not-allowed':
          message.error('Email/password accounts are not enabled. Please contact support.');
          break;
        case 'auth/too-many-requests':
          message.error('Too many requests. Please try again later.');
          break;
        default:
          message.error('Failed to sign up. Please try again.');
          console.error('Sign-up error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle(tenantType);
      // Navigation will be handled by the auth provider based on email verification status
      message.success('Account created with Google successfully!');
    } catch (error) {
      message.error('Failed to sign up with Google. Please try again.');
      console.error('Google sign-up error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const validateConfirmPassword = (_, value) => {
    if (value && value !== form.getFieldValue('password')) {
      return Promise.reject(new Error('The two passwords do not match!'));
    }
    return Promise.resolve();
  };

  const tabItems = [
    {
      key: 'employer',
      label: 'Healthcare Facility',
    },
    {
      key: 'agency',
      label: 'Staffing Agency',
    },
  ];

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-background-overlay">
          <div className="auth-background-content">
            <h1>Join VitalStaf</h1>
            <p>Start connecting healthcare professionals with opportunities</p>
            <div className="auth-features">
              <div className="feature-item">
                <div className="feature-icon">
                  <BankOutlined />
                </div>
                <span>Healthcare Facilities</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <UserOutlined />
                </div>
                <span>Medical Professionals</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <TeamOutlined />
                </div>
                <span>Staffing Agencies</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="auth-header">
            <h2>Create Account</h2>
            <p>Join our platform to get started</p>
          </div>
          
          <div className="auth-tabs-container">
            <Tabs 
              defaultActiveKey="employer" 
              items={tabItems}
              onChange={setTenantType}
              centered
              className="auth-tabs"
            />
          </div>
          
          <Form 
            form={form} 
            name="signup" 
            onFinish={onFinish} 
            layout="vertical" 
            className="auth-form"
          >
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input 
                size="large"
                placeholder="Enter your email address" 
                prefix={<MailOutlined />}
              />
            </Form.Item>
            
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter your password!' },
                { min: 6, message: 'Password must be at least 6 characters long!' }
              ]}
            >
              <Input.Password 
                size="large"
                placeholder="Enter your password"
                prefix={<LockOutlined />}
              />
            </Form.Item>
            
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password!' },
                { validator: validateConfirmPassword }
              ]}
            >
              <Input.Password 
                size="large"
                placeholder="Confirm your password"
                prefix={<LockOutlined />}
              />
            </Form.Item>
            
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                className="auth-form-button signin-button" 
                size="large"
                loading={loading}
                block
              >
                Create Account
              </Button>
            </Form.Item>
            
            <Divider className="auth-divider">
              <span>or continue with</span>
            </Divider>
            
            <Form.Item>
              <Button 
                type="default" 
                onClick={handleGoogleSignUp} 
                className="auth-form-button google-button" 
                size="large"
                loading={googleLoading}
                block
                icon={<GoogleOutlined />}
              >
                Sign up with Google
              </Button>
            </Form.Item>
            
            <div className="auth-form-footer">
              Already have an account?{' '}
              <Link to="/login" className="auth-form-link">
                Sign in here
              </Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default SignUpScreen;
